import { useState, useEffect, useRef } from "react";

// ─── CONFIG ───────────────────────────────────────────────────────────────────
const LIVE_MODE = true;
const PROXY_URL = "/api";

// ─── DESIGN TOKENS ────────────────────────────────────────────────────────────
const NAV = "#0b1f3a";
const MID = "#1a56c4";
const SKY = "#00a8e8";
const GRAY = "#64748b";
const LGRAY = "#e2e8f0";
const OFF = "#f4f7fb";
const GREEN = "#10b981";
const AMBER = "#f59e0b";
const RED = "#ef4444";

const STATUS = {
  open:    { bg:"#eff6ff", fg:"#1d4ed8", dot:"#3b82f6", label:"Open" },
  pending: { bg:"#fffbeb", fg:"#92400e", dot:"#f59e0b", label:"Pending" },
  solved:  { bg:"#ecfdf5", fg:"#065f46", dot:"#10b981", label:"Solved" },
  new:     { bg:"#fdf4ff", fg:"#6b21a8", dot:"#8b5cf6", label:"New" },
  closed:  { bg:"#f1f5f9", fg:"#475569", dot:"#94a3b8", label:"Closed" },
};
const PRIORITY = {
  urgent: { color:RED,   label:"🔴 Urgent" },
  high:   { color:AMBER, label:"🟠 High" },
  normal: { color:MID,   label:"🔵 Normal" },
  low:    { color:GRAY,  label:"⚪ Low" },
};

const inp = { width:"100%", border:`1.5px solid ${LGRAY}`, borderRadius:8, padding:"11px 13px", fontSize:15, fontFamily:"inherit", outline:"none", boxSizing:"border-box", background:"#fff" };
const lbl = { display:"block", fontSize:10, fontWeight:700, letterSpacing:1, textTransform:"uppercase", color:GRAY, marginBottom:5 };

// ─── GLOBAL CSS ───────────────────────────────────────────────────────────────
const GlobalStyles = () => (
  <style>{`
    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
    html, body, #root { height: 100%; }
    @keyframes spin { to { transform: rotate(360deg); } }
    ::-webkit-scrollbar { display: none; }
    * { scrollbar-width: none; }

    .tlm-shell { height: 100vh; height: 100dvh; display: flex; flex-direction: column; font-family: system-ui, -apple-system, sans-serif; overflow: hidden; background: ${OFF}; }
    .tlm-header { background: ${NAV}; height: 56px; display: flex; align-items: center; justify-content: space-between; padding: 0 22px; position: fixed; top: 0; left: 0; right: 0; z-index: 100; box-shadow: 0 2px 12px rgba(0,0,0,.3); flex-shrink: 0; }
    .tlm-header-spacer { height: 56px; flex-shrink: 0; }
    .tlm-header-divider { width: 1px; height: 18px; background: rgba(255,255,255,.2); }
    .tlm-header-portal-label { font-size: 10px; font-weight: 700; letter-spacing: 2px; text-transform: uppercase; color: rgba(255,255,255,.4); }
    .tlm-header-name { font-size: 12px; font-weight: 600; color: #fff; }
    .tlm-header-org { font-size: 10px; color: rgba(255,255,255,.4); }
    .tlm-header-user-text { text-align: right; }
    .tlm-dashboard { max-width: 1140px; margin: 0 auto; padding: 0 14px 16px; width: 100%; display: flex; flex-direction: column; min-height: 100%; }
    .tlm-sidebar { display: none; }
    .tlm-stats-desktop { display: grid; grid-template-columns: repeat(6,1fr); gap: 8px; margin-bottom: 18px; }
    .tlm-stats-mobile  { display: none; }
    .tlm-desktop-sticky { position: sticky; top: 0; z-index: 50; background: ${OFF}; padding: 16px 0 0; }
    .tlm-table-header { display: grid; grid-template-columns: 72px 1fr 92px 82px 106px; padding: 9px 16px; background: ${OFF}; border-bottom: 1px solid ${LGRAY}; }
    .tlm-ticket-row   { display: grid; grid-template-columns: 72px 1fr 92px 82px 106px; padding: 11px 16px; align-items: center; min-width: 0; }
    .tlm-ticket-card  { display: none; }
    .tlm-fab { display: none; }
    .tlm-mobile-only { display: none; }
    .tlm-reply-btn { width: auto; }

    @media (max-width: 700px) {
      .tlm-header { padding: 0 14px; }
      .tlm-header-divider { display: none; }
      .tlm-header-portal-label { display: none; }
      .tlm-header-user-text { display: none; }
      .tlm-dashboard { display: flex; flex-direction: column; padding: 0; max-width: 100%; min-height: 100%; }
      .tlm-sidebar   { display: none; }
      .tlm-main-scroll { flex: 1; overflow-y: auto; padding-bottom: calc(84px + env(safe-area-inset-bottom)); padding-top: 0; -webkit-overflow-scrolling: touch; }
      .tlm-stats-desktop { display: none; }
      .tlm-stats-mobile  { display: block; overflow-x: auto; padding: 8px 14px 4px; }
      .tlm-stats-mobile-inner { display: grid; grid-template-columns: repeat(6,1fr); gap: 6px; min-width: 0; }
      .tlm-table-header { display: none; }
      .tlm-ticket-row   { display: none; }
      .tlm-ticket-card  { display: block; padding: 14px 16px 14px 13px; border-bottom: 1px solid ${LGRAY}; cursor: pointer; background: #fff; }
      .tlm-toolbar { display: none; }
      .tlm-mobile-search { display: flex; gap: 8px; padding: 10px 14px 6px; }
      .tlm-mobile-section-label { display: block; padding: 2px 14px 8px; font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; color: ${GRAY}; }
      .tlm-mobile-sticky { position: fixed; top: 56px; left: 0; right: 0; z-index: 50; background: ${OFF}; box-shadow: 0 2px 8px rgba(0,0,0,.06); }
      .tlm-mobile-sticky-spacer { height: 120px; display: block; }
      .tlm-fab { display: flex; align-items: center; gap: 8px; position: fixed; bottom: calc(20px + env(safe-area-inset-bottom)); right: 20px; z-index: 200; background: linear-gradient(135deg,${MID},${SKY}); color: #fff; border: none; border-radius: 28px; padding: 14px 22px; font-weight: 700; font-size: 15px; font-family: inherit; cursor: pointer; box-shadow: 0 4px 20px rgba(26,86,196,.5); }
      .tlm-desktop-new-btn { display: none; }
      .tlm-modal-box { border-radius: 16px 16px 0 0 !important; max-width: 100% !important; }
      .tlm-modal-overlay { align-items: flex-end !important; padding: 0 !important; }
      .tlm-modal-handle { display: block; }
      .tlm-detail-wrap { padding: 12px; }
      .tlm-reply-btn { width: 100%; }
      .tlm-thread-body { padding-left: 0 !important; margin-top: 6px; }
      .tlm-detail-bottom-pad { margin-bottom: 84px; }
      .tlm-toast { bottom: calc(90px + env(safe-area-inset-bottom)) !important; }
      .tlm-mobile-only { display: block; }
    }

    @media (min-width: 701px) {
      .tlm-main-scroll { flex: 1; overflow-y: auto; min-height: 0; }
      .tlm-mobile-search { display: none; }
      .tlm-mobile-section-label { display: none; }
      .tlm-modal-handle { display: none; }
      .tlm-toolbar { display: flex; }
      .tlm-mobile-sticky { display: none; }
      .tlm-mobile-sticky-spacer { display: none; }
    }
  `}</style>
);

// ─── DEMO DATA ────────────────────────────────────────────────────────────────
const DEMO_USERS = [
  { id:1,  email:"jane@acmecorp.com",   password:"demo123",  name:"Jane Smith", org:"Acme Corporation",   org_id:101, initials:"JS", role:"end-user" },
  { id:2,  email:"bob@rivertech.com",   password:"demo123",  name:"Bob Rivera", org:"River Technologies", org_id:102, initials:"BR", role:"end-user" },
  { id:99, email:"admin@tellemica.com", password:"admin123", name:"Admin User", org:"Tellemica",          org_id:1,   initials:"AU", role:"admin" },
];
const DEMO_TICKETS = [
  { id:"TLM-1042", zendesk_id:1042, subject:"Add 5 new wireless lines for sales team", type:"order", status:"open", priority:"normal", opened:"Mar 18, 2025", updated:"Mar 24, 2025", org_id:101,
    thread:[
      { from:"Jane Smith", role:"customer", time:"Mar 18 · 9:14am", body:"Hi team, we have 5 new sales reps starting April 1st. They each need an iPhone 16 Pro with Unlimited Premium plan. Shipping to our Exton office." },
      { from:"Mike Foley — Tellemica", role:"agent", time:"Mar 18 · 10:32am", body:"Got it! Order submitted. Expected activation March 28th. Can you confirm shipping is still 602 Jeffers Circle?" },
      { from:"Jane Smith", role:"customer", time:"Mar 18 · 11:05am", body:"Yes, same address. Thanks!" },
    ]},
  { id:"TLM-1039", zendesk_id:1039, subject:"Upgrade 12 field tech lines to 5G Unlimited", type:"order", status:"pending", priority:"high", opened:"Mar 14, 2025", updated:"Mar 22, 2025", org_id:101,
    thread:[
      { from:"Jane Smith", role:"customer", time:"Mar 14 · 2:10pm", body:"We want to upgrade our 12 field tech lines to 5G Unlimited. Can you confirm pricing first?" },
      { from:"Ed Foley — Tellemica", role:"agent", time:"Mar 15 · 9:00am", body:"Moving to Unlimited Premium 5G would be $45/line with your discount — net increase ~$180/mo. There's a promo expiring March 31st that could help. Want a comparison doc?" },
    ]},
  { id:"TLM-1036", zendesk_id:1036, subject:"Deactivate 2 lines — departed employees", type:"account", status:"open", priority:"urgent", opened:"Mar 10, 2025", updated:"Mar 20, 2025", org_id:101,
    thread:[
      { from:"Jane Smith", role:"customer", time:"Mar 10 · 8:30am", body:"Two employees left last week. Please deactivate lines ending in 4421 and 8873 immediately." },
      { from:"Celina Rothwell — Tellemica", role:"agent", time:"Mar 10 · 9:15am", body:"Deactivation submitted for both lines — they'll be off by end of today. Would you like us to initiate device buyback?" },
    ]},
  { id:"TLM-1031", zendesk_id:1031, subject:"Fixed Wireless Internet — new warehouse", type:"order", status:"open", priority:"normal", opened:"Mar 5, 2025", updated:"Mar 19, 2025", org_id:101,
    thread:[
      { from:"Jane Smith", role:"customer", time:"Mar 5 · 3:00pm", body:"Opening a new warehouse in Malvern next month. Need Fixed Wireless before April 15th. Address: 100 Industrial Blvd, Malvern PA 19355." },
      { from:"Mike Foley — Tellemica", role:"agent", time:"Mar 6 · 10:00am", body:"Fixed Wireless is available at that address. Site survey March 12th, install March 28th — two weeks before your deadline!" },
    ]},
  { id:"TLM-1028", zendesk_id:1028, subject:"Update billing contact to new CFO", type:"account", status:"pending", priority:"normal", opened:"Feb 28, 2025", updated:"Mar 15, 2025", org_id:101,
    thread:[
      { from:"Jane Smith", role:"customer", time:"Feb 28 · 11:20am", body:"New billing contact: Robert Chen, robert.chen@acmecorp.com, (610) 555-9100." },
      { from:"Celina Rothwell — Tellemica", role:"agent", time:"Mar 1 · 9:05am", body:"Updated! Robert may get a verification email to confirm. I'll follow up if anything is needed." },
    ]},
  { id:"TLM-1019", zendesk_id:1019, subject:"Device buyback — 8 retired iPhones", type:"order", status:"solved", priority:"normal", opened:"Feb 10, 2025", updated:"Feb 24, 2025", org_id:101,
    thread:[
      { from:"Jane Smith", role:"customer", time:"Feb 10 · 10:00am", body:"We have 8 iPhone 13s being replaced. Can you handle the buyback?" },
      { from:"Ed Foley — Tellemica", role:"agent", time:"Feb 10 · 11:30am", body:"Absolutely! Looking at ~$120-150/device. I'll ship prepaid return labels to your office." },
      { from:"Jane Smith", role:"customer", time:"Feb 22 · 2:00pm", body:"Devices sent — tracking #1Z999AA10123456784." },
      { from:"Ed Foley — Tellemica", role:"agent", time:"Feb 24 · 9:00am", body:"Processed! $1,104 buyback credit applied to your account. Closing this ticket — reach out anytime!" },
    ]},
  { id:"TLM-1041", zendesk_id:1041, subject:"IoT sensor setup — 3 warehouse locations", type:"order", status:"open", priority:"high", opened:"Mar 20, 2025", updated:"Mar 25, 2025", org_id:102,
    thread:[
      { from:"Bob Rivera", role:"customer", time:"Mar 20 · 10:00am", body:"We need IoT sensors deployed at our 3 PA warehouses. Can you scope this out?" },
      { from:"Thomas Quinn — Tellemica", role:"agent", time:"Mar 20 · 2:00pm", body:"Absolutely — I'll have a proposal to you by end of week. Can you confirm the square footage for each location?" },
    ]},
  { id:"TLM-1033", zendesk_id:1033, subject:"Fleet tracking — 15 vehicles", type:"order", status:"solved", priority:"normal", opened:"Mar 1, 2025", updated:"Mar 14, 2025", org_id:102,
    thread:[
      { from:"Bob Rivera", role:"customer", time:"Mar 1 · 9:00am", body:"Need GPS fleet tracking on our 15 delivery vehicles." },
      { from:"Shawn Herncane — Tellemica", role:"agent", time:"Mar 2 · 9:00am", body:"Units shipped and installed. All 15 vehicles now live on the dashboard!" },
    ]},
];

// ─── ZENDESK API ──────────────────────────────────────────────────────────────
const ZendeskAPI = {
  _mapTicket(t) {
    return { id:t.id, zendesk_id:t.zendesk_id||t.id, subject:t.subject, type:t.type||"order", status:t.status, priority:t.priority||"normal", tags:t.tags||[], opened:t.opened||new Date(t.created_at).toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"})||"N/A", updated:t.updated||new Date(t.updated_at).toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"})||"N/A", updated_at:t.updated_at||"", org_id:t.org_id||t.organization_id, thread:[] };
  },
  async getTickets(user) {
    if (!LIVE_MODE) {
      await new Promise(r => setTimeout(r, 600));
      if (user.is_staff || user.role === "admin") return DEMO_TICKETS;
      return DEMO_TICKETS.filter(t => t.org_id === user.org_id);
    }
    const userId = user.zendesk_id || user.id || "";
    const params = new URLSearchParams({ user_id: userId });
    const res = await fetch(`${PROXY_URL}/tickets?${params.toString()}`, {
      headers:{"Content-Type":"application/json"}
    });
    if (!res.ok) throw new Error("Failed to fetch tickets");
    const data = await res.json();
    return (data.tickets||[]).map(this._mapTicket);
  },
  async searchTickets(user, query) {
    if (!LIVE_MODE) {
      await new Promise(r => setTimeout(r, 300));
      const q = query.toLowerCase();
      return DEMO_TICKETS.filter(t => t.subject.toLowerCase().includes(q) || String(t.id).toLowerCase().includes(q));
    }
    const userId = user.zendesk_id || user.id || "";
    const params = new URLSearchParams({ user_id: userId, q: query });
    const res = await fetch(`${PROXY_URL}/search?${params.toString()}`, {
      headers:{"Content-Type":"application/json"}
    });
    if (!res.ok) throw new Error("Search failed");
    const data = await res.json();
    return (data.tickets||[]).map(this._mapTicket);
  },
  async getThread(ticketId) {
    if (!LIVE_MODE) {
      await new Promise(r => setTimeout(r, 400));
      const ticket = DEMO_TICKETS.find(t => t.zendesk_id === ticketId || t.id === ticketId);
      return ticket?.thread || [];
    }
    const res = await fetch(`${PROXY_URL}/comments?ticket_id=${ticketId}`);
    if (!res.ok) throw new Error("Failed to fetch thread");
    const data = await res.json();
    const comments = (data.comments || data.thread || []).map(c => ({ from:c.from||"Unknown", role:c.role||"customer", time:c.time||"N/A", created_at:c.created_at||null, body:c.body||"", attachments:c.attachments||[] }));
    return { comments, collaborators: data.collaborators || [] };
  },
  async createTicket(user, subject, description, type, priority, cc="", attachment=null) {
    if (!LIVE_MODE) {
      await new Promise(r => setTimeout(r, 800));
      const now = new Date().toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"});
      return { id:`${1050+Math.floor(Math.random()*100)}`, zendesk_id:1055, subject, type, status:"new", priority, opened:now, updated:now, org_id:user.org_id, thread:[{from:user.name,role:"customer",time:now,body:description}] };
    }
    const res = await fetch(`${PROXY_URL}/create-ticket`, {
      method:"POST",
      headers:{"Content-Type":"application/json"},
      body:JSON.stringify({subject,description,type,priority,user_id:user.zendesk_id||user.id,org_id:user.org_id||user.organization_id,cc,attachment:attachment||null})
    });
    if (!res.ok) { const err = await res.json().catch(()=>({})); throw new Error(err.error||"Failed to create ticket"); }
    const data = await res.json(); const t = data.ticket;
    const now = new Date().toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"});
    return { id:`${t.id}`, zendesk_id:t.id, subject:t.subject, type, status:"new", priority, opened:now, updated:now, org_id:t.organization_id, thread:[{from:user.name,role:"customer",time:now,body:description}] };
  },
  async addReply(ticketId, body, user, attachment=null) {
    if (!LIVE_MODE) { await new Promise(r => setTimeout(r, 500)); return true; }
    const res = await fetch(`${PROXY_URL}/reply`, {
      method:"POST",
      headers:{"Content-Type":"application/json"},
      body:JSON.stringify({ticket_id:ticketId,body,author_id:user.zendesk_id||user.id,attachment:attachment||null})
    });
    return res.ok;
  },
  async authenticate(email, password) {
    if (!LIVE_MODE) {
      await new Promise(r => setTimeout(r, 700));
      const u = DEMO_USERS.find(u => u.email===email.trim().toLowerCase() && u.password===password);
      if (!u) throw new Error("Invalid credentials");
      return u;
    }
    const res = await fetch(`${PROXY_URL}/auth`, { method:"POST", headers:{"Content-Type":"application/json"}, body:JSON.stringify({email,password}) });
    if (!res.ok) throw new Error("Invalid email or password");
    return await res.json();
  },
  async requestAccess(formData) {
    if (!LIVE_MODE) {
      await new Promise(r => setTimeout(r, 900));
      try {
        await fetch("https://formspree.io/f/xvzlnkoq", { method:"POST", headers:{"Content-Type":"application/json","Accept":"application/json"}, body:JSON.stringify({first_name:formData.first,last_name:formData.last,company:formData.co,email:formData.email,phone:formData.phone,reason:formData.reason,_subject:`Portal Access Request - ${formData.first} ${formData.last} (${formData.co})`}) });
      } catch(e) { console.log("Formspree error:",e); }
      return true;
    }
    const res = await fetch(`${PROXY_URL}/access-request`, { method:"POST", headers:{"Content-Type":"application/json"}, body:JSON.stringify(formData) });
    return res.ok;
  }
};

// ─── SMALL COMPONENTS ─────────────────────────────────────────────────────────
// ─── TIME HELPER ─────────────────────────────────────────────────────────────
function formatLocalTime(isoString) {
  if (!isoString) return "N/A";
  const d = new Date(isoString);
  const date = d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  const time = d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true }).toLowerCase();
  return `${date} · ${time}`;
}

function StatusPill({ status }) {
  const c = STATUS[status] || STATUS.open;
  return (
    <span style={{ display:"inline-flex", alignItems:"center", gap:4, fontSize:11, fontWeight:700, textTransform:"uppercase", letterSpacing:.8, padding:"4px 10px", borderRadius:20, background:c.bg, color:c.fg, whiteSpace:"nowrap" }}>
      <span style={{ width:5, height:5, borderRadius:"50%", background:c.dot, flexShrink:0 }} />{c.label}
    </span>
  );
}
function Av({ text, size=34, colors=[NAV,MID] }) {
  return <div style={{ width:size, height:size, borderRadius:"50%", background:`linear-gradient(135deg,${colors[0]},${colors[1]})`, display:"flex", alignItems:"center", justifyContent:"center", color:"#fff", fontWeight:700, fontSize:size*.3, flexShrink:0 }}>{text}</div>;
}
function Spinner() {
  return <div style={{ display:"flex", alignItems:"center", justifyContent:"center", padding:48 }}><div style={{ width:28, height:28, border:`3px solid ${LGRAY}`, borderTop:`3px solid ${MID}`, borderRadius:"50%", animation:"spin .7s linear infinite" }} /></div>;
}
function ModeBadge() {
  if (!LIVE_MODE) return null;
  return <div style={{ position:"fixed", bottom:"calc(12px + env(safe-area-inset-bottom))", left:12, background:GREEN, color:"#fff", fontSize:10, fontWeight:700, letterSpacing:1, textTransform:"uppercase", padding:"4px 10px", borderRadius:20, zIndex:999, opacity:.85 }}>● Live</div>;
}

// ─── HEADER ───────────────────────────────────────────────────────────────────
function Header({ user, onLogout }) {
  return (
    <div className="tlm-header">
      <div style={{ display:"flex", alignItems:"center", gap:10 }}>
        <span style={{ fontWeight:900, fontSize:15, color:"#fff", letterSpacing:3 }}>TELLEMICA</span>
        <span className="tlm-header-divider" />
        <span className="tlm-header-portal-label">Client Portal</span>
      </div>
      {user && (
        <div style={{ display:"flex", alignItems:"center", gap:8 }}>
          <div className="tlm-header-user-text">
            <div className="tlm-header-name">{user.name}</div>
            <div className="tlm-header-org">{user.org}</div>
          </div>
          <Av text={user.initials} size={32} />
          <button onClick={onLogout} style={{ background:"rgba(255,255,255,.1)", border:"1px solid rgba(255,255,255,.18)", color:"rgba(255,255,255,.7)", fontSize:10, padding:"5px 11px", borderRadius:6, cursor:"pointer", fontWeight:700, letterSpacing:1, textTransform:"uppercase", fontFamily:"inherit", whiteSpace:"nowrap" }}>
            Sign Out
          </button>
        </div>
      )}
    </div>
  );
}

// ─── MODAL ────────────────────────────────────────────────────────────────────
function CcTagInput({ tags, setTags, history = [], onForget }) {
  const [input, setInput] = useState("");
  const [ccErr, setCcErr] = useState("");
  const [showDrop, setShowDrop] = useState(false);
  const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  function addTag(val) {
    const emails = val.split(/[,;]+/).map(e => e.trim().toLowerCase()).filter(Boolean);
    const valid = [], invalid = [];
    emails.forEach(e => emailRe.test(e) ? valid.push(e) : e && invalid.push(e));
    setTags([...new Set([...tags, ...valid])]);
    setInput("");
    setCcErr(invalid.length ? "Invalid: " + invalid.join(", ") : "");
  }

  function addOne(email) {
    setTags([...new Set([...tags, email])]);
    setInput("");
    setShowDrop(false);
  }

  function onKeyDown(e) {
    if (["Enter",","," "].includes(e.key)) {
      e.preventDefault();
      if (input.trim()) addTag(input);
    } else if (e.key === "Tab" && input.trim()) {
      // Only intercept Tab if there's an email being typed — add it as a tag
      // If CC field is empty, Tab passes through normally to next field
      e.preventDefault();
      addTag(input);
    } else if (e.key === "Backspace" && !input && tags.length) {
      setTags(tags.slice(0,-1));
    } else if (e.key === "Escape") {
      setShowDrop(false);
    }
  }

  // Suggestions not already added as tags
  const available = history.filter(h => !tags.includes(h));
  // Autocomplete matches for what's currently typed (only shown while typing)
  const typed = input.trim().toLowerCase();
  const matches = typed
    ? available.filter(h => h.includes(typed)).slice(0, 6)
    : [];

  return (
    <div>
      <div style={{ position:"relative" }}>
        <div onClick={()=>document.getElementById("tlm-cc-input").focus()}
          style={{ display:"flex", flexWrap:"wrap", gap:5, border:"1.5px solid "+LGRAY, borderRadius:8, padding:"7px 10px", minHeight:44, background:"#fff", cursor:"text", alignItems:"center" }}>
          {tags.map((tag,i) => (
            <span key={i} style={{ display:"inline-flex", alignItems:"center", gap:4, background:"#eff6ff", color:MID, borderRadius:20, padding:"3px 10px", fontSize:12, fontWeight:600 }}>
              {tag}
              <span onClick={()=>setTags(tags.filter((_,j)=>j!==i))} style={{ cursor:"pointer", fontSize:16, lineHeight:1, color:MID }}>×</span>
            </span>
          ))}
          <input id="tlm-cc-input" value={input}
            onChange={e=>{setInput(e.target.value);setCcErr("");setShowDrop(true);}}
            onKeyDown={onKeyDown} onFocus={()=>setShowDrop(true)}
            onBlur={()=>{ if(input.trim())addTag(input); setTimeout(()=>setShowDrop(false),150); }}
            placeholder={tags.length===0?"colleague@company.com, another@company.com…":""}
            style={{ border:"none", outline:"none", fontSize:13, fontFamily:"inherit", flex:1, minWidth:140, background:"transparent", padding:"2px 0" }} />
        </div>
        {showDrop && matches.length > 0 && (
          <div style={{ position:"absolute", top:"100%", left:0, right:0, marginTop:4, background:"#fff", border:"1px solid "+LGRAY, borderRadius:8, boxShadow:"0 6px 20px rgba(0,0,0,.12)", zIndex:20, overflow:"hidden" }}>
            {matches.map((email,i) => (
              <div key={i}
                style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:8, padding:"9px 12px", fontSize:13, color:"#1e293b", cursor:"pointer", borderBottom:i<matches.length-1?"1px solid "+OFF:"none" }}
                onMouseEnter={e=>e.currentTarget.style.background=OFF}
                onMouseLeave={e=>e.currentTarget.style.background="#fff"}>
                <span onMouseDown={(e)=>{e.preventDefault();addOne(email);}} style={{ flex:1 }}>{email}</span>
                {onForget && (
                  <span onMouseDown={(e)=>{e.preventDefault();e.stopPropagation();onForget(email);}}
                    title="Forget this email"
                    style={{ cursor:"pointer", fontSize:15, lineHeight:1, color:GRAY, padding:"0 2px" }}>×</span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      {ccErr && <div style={{ fontSize:11, color:RED, marginTop:3 }}>{ccErr}</div>}
      <div style={{ fontSize:10, color:GRAY, marginTop:3 }}>Press Enter or comma to add · Backspace to remove{history.length>0?" · Tap a suggestion to add":""}</div>
    </div>
  );
}


// ─── FILE ATTACHMENT HELPER ───────────────────────────────────────────────────
function FileAttachment({ file, setFile }) {
  const MAX_MB = 20;
  const allowed = [
    'image/jpeg','image/png','image/gif','image/webp',
    'application/pdf',
    'application/msword','application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel','application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-powerpoint','application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'text/plain','text/csv',
    'message/rfc822',           // .eml
    'application/vnd.ms-outlook', // .msg
    'application/zip','application/x-zip-compressed',
  ];
  const allowedExts = ['jpg','jpeg','png','gif','webp','pdf','doc','docx','xls','xlsx','ppt','pptx','txt','csv','eml','msg','zip'];

  function handleFile(e) {
    const f = e.target.files[0];
    if (!f) return;
    if (f.size > MAX_MB * 1024 * 1024) { alert(`File too large — max ${MAX_MB}MB`); return; }
    // Check by MIME type first, then fall back to extension (browsers often misidentify .eml/.msg)
    const ext = f.name.split('.').pop().toLowerCase();
    if (f.type && !allowed.includes(f.type) && !allowedExts.includes(ext)) {
      alert('File type not supported. Supported: images, PDF, Word, Excel, PowerPoint, text, email files (.eml, .msg), and ZIP.');
      return;
    }
    const reader = new FileReader();
    reader.onload = ev => setFile({ filename: f.name, contentType: f.type, data: ev.target.result.split(',')[1], size: f.size });
    reader.readAsDataURL(f);
  }

  return (
    <div>
      <label style={{ display:"flex", alignItems:"center", gap:8, cursor:"pointer", border:`1.5px dashed ${LGRAY}`, borderRadius:8, padding:"10px 14px", background:"#fafafa", transition:"border-color .2s" }}
        onMouseEnter={e=>e.currentTarget.style.borderColor=MID}
        onMouseLeave={e=>e.currentTarget.style.borderColor=LGRAY}>
        <span style={{ fontSize:18 }}>📎</span>
        <span style={{ fontSize:13, color:GRAY }}>{file ? file.filename : "Attach a file (optional, max 20MB)"}</span>
        <input type="file" onChange={handleFile} style={{ display:"none" }} accept=".jpg,.jpeg,.png,.gif,.webp,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.eml,.msg,.zip" />
      </label>
      {file && (
        <div style={{ display:"flex", alignItems:"center", gap:8, marginTop:6, fontSize:12, color:MID }}>
          <span>📄 {file.filename} ({(file.size/1024).toFixed(0)}KB)</span>
          <span onClick={()=>setFile(null)} style={{ cursor:"pointer", color:RED, fontWeight:700 }}>× Remove</span>
        </div>
      )}
    </div>
  );
}

function Modal({ onClose, mSub, setMSub, mDesc, setMDesc, mPri, setMPri, mCc, setMCc, mDone, mBusy, onSubmit, mFile, setMFile, ccHistory, onForgetCc }) {
  return (
    <div className="tlm-modal-overlay" style={{ position:"fixed", inset:0, background:"rgba(0,0,0,.5)", zIndex:300, display:"flex", alignItems:"center", justifyContent:"center", padding:20 }}
      onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="tlm-modal-box" style={{ background:"#fff", borderRadius:14, width:"100%", maxWidth:500, boxShadow:"0 -4px 40px rgba(0,0,0,.15)", maxHeight:"92vh", display:"flex", flexDirection:"column", overflow:"hidden" }}>
        <div className="tlm-modal-handle" style={{ width:36, height:4, borderRadius:2, background:LGRAY, margin:"10px auto 0", flexShrink:0 }} />
        <div style={{ background:NAV, padding:"16px 20px", display:"flex", alignItems:"center", justifyContent:"space-between", flexShrink:0 }}>
          <span style={{ fontWeight:700, fontSize:13, textTransform:"uppercase", letterSpacing:1, color:"#fff" }}>Submit New Request</span>
          <button onClick={onClose} style={{ background:"rgba(255,255,255,.1)", border:"none", color:"rgba(255,255,255,.7)", width:30, height:30, borderRadius:6, cursor:"pointer", fontSize:16, fontFamily:"inherit" }}>✕</button>
        </div>
        <div style={{ padding:20, overflowY:"auto" }}>
          {mDone ? (
            <div style={{ background:"#ecfdf5", border:"1px solid #a7f3d0", borderRadius:10, padding:24, textAlign:"center" }}>
              <div style={{ fontSize:30, marginBottom:8 }}>🎫</div>
              <div style={{ fontWeight:700, color:"#065f46", fontSize:15, marginBottom:6 }}>Request Submitted!</div>
              <div style={{ fontSize:13, color:"#047857", lineHeight:1.6 }}>Ticket created and your Tellemica account manager has been notified.</div>
            </div>
          ) : (
            <div>
              <div style={{ marginBottom:12 }}><label style={lbl}>Request Name</label><input style={inp} value={mSub} onChange={e=>setMSub(e.target.value)} placeholder="e.g. Add 3 new lines for new hires" /></div>
              <div style={{ marginBottom:12 }}>
                <label style={lbl}>Priority</label>
                <select style={inp} value={mPri} onChange={e=>setMPri(e.target.value)}>
                  <option value="low">Low</option>
                  <option value="normal">Normal</option>
                  <option value="high">High — Within 24 hrs</option>
                  <option value="urgent">Urgent — Business Critical</option>
                </select>
              </div>
              <div style={{ marginBottom:12 }}>
                <label style={lbl}>CC <span style={{ fontWeight:400, textTransform:"none", letterSpacing:0, fontSize:10, color:GRAY }}>(optional — add multiple)</span></label>
                <CcTagInput tags={mCc} setTags={setMCc} history={ccHistory||[]} onForget={onForgetCc} />
              </div>
              <div style={{ marginBottom:12 }}><label style={lbl}>Description</label><textarea style={{ ...inp, height:100, resize:"vertical", display:"block" }} value={mDesc} onChange={e=>setMDesc(e.target.value)} placeholder="Describe your request in detail…" /></div>
              <div style={{ marginBottom:20 }}><label style={lbl}>Attachment <span style={{ fontWeight:400, textTransform:"none", letterSpacing:0, fontSize:10, color:GRAY }}>(optional)</span></label><FileAttachment file={mFile} setFile={setMFile} /></div>
              <div style={{ display:"flex", gap:10 }}>
                <button onClick={onClose} style={{ flex:"0 0 auto", background:"transparent", border:`1.5px solid ${LGRAY}`, color:GRAY, padding:"11px 18px", borderRadius:8, fontSize:13, fontWeight:600, cursor:"pointer", fontFamily:"inherit" }}>Cancel</button>
                <button onClick={onSubmit} disabled={mBusy} style={{ flex:1, background:mBusy?GRAY:MID, color:"#fff", border:"none", padding:"11px 20px", borderRadius:8, fontWeight:700, fontSize:14, cursor:mBusy?"not-allowed":"pointer", fontFamily:"inherit" }}>
                  {mBusy ? "Submitting…" : "Submit Request →"}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── INSTALL BANNER ───────────────────────────────────────────────────────────
// Shows "Add to Home Screen" prompt on mobile only (iOS + Android).
// Auto-hides on desktop, after install, or after dismissal (remembered 7 days).
function InstallBanner() {
  const [show, setShow] = useState(false);
  const [platform, setPlatform] = useState("other");

  useEffect(() => {
    const DISMISS_KEY = "tlm_install_banner_dismissed_at";
    const DISMISS_DAYS = 7;

    function isStandalone() {
      if (window.navigator.standalone === true) return true;
      if (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) return true;
      return false;
    }
    function recentlyDismissed() {
      try {
        const ts = localStorage.getItem(DISMISS_KEY);
        if (!ts) return false;
        const days = (Date.now() - parseInt(ts, 10)) / (1000 * 60 * 60 * 24);
        return days < DISMISS_DAYS;
      } catch { return false; }
    }
    function detectPlatform() {
      const ua = navigator.userAgent || "";
      if (/iPad|iPhone|iPod/.test(ua) && !window.MSStream) return "ios";
      if (/Android/i.test(ua)) return "android";
      return "other";
    }

    const p = detectPlatform();
    const isMobile = window.innerWidth < 768 && p !== "other";
    if (isStandalone() || recentlyDismissed() || !isMobile) return;
    setPlatform(p);
    setShow(true);
  }, []);

  function dismiss() {
    setShow(false);
    try { localStorage.setItem("tlm_install_banner_dismissed_at", String(Date.now())); } catch {}
  }

  if (!show) return null;

  const instructions = platform === "ios" ? (
    <span>
      Tap the <strong>Share</strong> button{" "}
      <svg width="13" height="16" viewBox="0 0 14 18" fill="none" style={{ display:"inline-block", verticalAlign:"-2px" }}>
        <path d="M7 12V2M7 2L3 6M7 2L11 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M2 10V15C2 15.5523 2.44772 16 3 16H11C11.5523 16 12 15.5523 12 15V10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
      </svg>
      {" "}at the bottom of Safari, then choose <strong>"Add to Home Screen"</strong>.
    </span>
  ) : (
    <span>
      Tap the <strong>menu</strong> (⋮) in the top-right of Chrome, then choose <strong>"Add to Home screen"</strong> or <strong>"Install app"</strong>.
    </span>
  );

  return (
    <div style={{
      position:"fixed", bottom:16, left:16, right:16, maxWidth:420, margin:"0 auto",
      background:"#fff", color:"#1a2332", borderRadius:14,
      boxShadow:"0 12px 40px rgba(0,0,0,.25)", padding:"14px 14px",
      border:"1px solid rgba(0,0,0,.08)", zIndex:9999,
      fontFamily:"system-ui, -apple-system, sans-serif",
      animation:"tlmSlideUp .4s ease-out"
    }}>
      <style>{`
        @keyframes tlmSlideUp { from { transform: translateY(30px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
      `}</style>
      <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:6 }}>
        <img src="/icons/apple-touch-icon.png" alt="" style={{ width:40, height:40, borderRadius:9, flexShrink:0 }} />
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ fontWeight:600, fontSize:14, lineHeight:1.2 }}>Install the Portal</div>
          <div style={{ fontSize:11, color:"#6b7280", marginTop:2 }}>Add to your home screen for one-tap access</div>
        </div>
        <button onClick={dismiss} aria-label="Dismiss" style={{
          background:"none", border:"none", color:"#9ca3af", fontSize:22, lineHeight:1,
          cursor:"pointer", padding:"4px 8px"
        }}>×</button>
      </div>
      <div style={{ fontSize:12, color:"#374151", lineHeight:1.5, marginTop:4 }}>
        {instructions}
      </div>
    </div>
  );
}

// ─── LOGIN ────────────────────────────────────────────────────────────────────
function Login({ onLogin }) {
  const [tab, setTab]     = useState("in");
  const [email, setEmail] = useState("");
  const [pw, setPw]       = useState("");
  const [err, setErr]     = useState("");
  const [busy, setBusy]   = useState(false);
  const [r, setR]         = useState({ first:"", last:"", co:"", email:"", phone:"", reason:"" });
  const [rErr, setRErr]   = useState("");
  const [rOk, setROk]     = useState(false);
  const [rBusy, setRBusy] = useState(false);

  async function login() {
    if (!email||!pw) { setErr("Please enter your email and password."); return; }
    setErr(""); setBusy(true);
    try { const user = await ZendeskAPI.authenticate(email,pw); onLogin(user); }
    catch(e) { setErr(e.message||"Invalid email or password."); }
    finally { setBusy(false); }
  }
  async function requestAccess() {
    if (!r.first||!r.last||!r.co||!r.email||!r.reason) { setRErr("Please fill in all required fields."); return; }
    setRErr(""); setRBusy(true);
    try { await ZendeskAPI.requestAccess(r); setROk(true); }
    catch { setRErr("Something went wrong. Please try again or call (610) 634-8100."); }
    finally { setRBusy(false); }
  }

  return (
    <div style={{ flex:1, background:`linear-gradient(135deg,${NAV},#0d2d5e,#0d3b8e)`, display:"flex", alignItems:"center", justifyContent:"center", padding:20, overflowY:"auto" }}>
      <div style={{ background:"#fff", borderRadius:14, boxShadow:"0 20px 60px rgba(0,0,0,.3)", width:"100%", maxWidth:420, overflow:"hidden" }}>
        <div style={{ background:NAV, padding:"26px 32px 20px", textAlign:"center" }}>
          <div style={{ fontWeight:900, fontSize:22, color:"#fff", letterSpacing:3, marginBottom:8 }}>TELLEMICA</div>
          <div style={{ fontSize:13, fontWeight:700, textTransform:"uppercase", letterSpacing:2, color:"#fff", marginBottom:3 }}>Client Portal</div>
          <div style={{ fontSize:11, color:"rgba(255,255,255,.35)", letterSpacing:1 }}>SECURE CLIENT ACCESS</div>
        </div>
        <div style={{ display:"flex", borderBottom:`2px solid ${LGRAY}` }}>
          {[["in","Sign In"],["req","Request Access"]].map(([k,label])=>(
            <div key={k} onClick={()=>setTab(k)} style={{ flex:1, padding:"13px 8px", textAlign:"center", fontSize:12, fontWeight:700, letterSpacing:1, textTransform:"uppercase", cursor:"pointer", color:tab===k?MID:GRAY, borderBottom:tab===k?`3px solid ${SKY}`:"3px solid transparent", marginBottom:-2 }}>{label}</div>
          ))}
        </div>
        <div style={{ padding:"24px 32px" }}>
          {tab==="in" && (
            <div>
              {err && <div style={{ background:"#fef2f2", border:"1px solid #fecaca", color:"#991b1b", borderRadius:7, padding:"9px 13px", fontSize:13, marginBottom:14 }}>{err}</div>}
              <div style={{ marginBottom:14 }}><label style={lbl}>Email</label><input style={inp} type="email" value={email} onChange={e=>setEmail(e.target.value)} onKeyDown={e=>e.key==="Enter"&&login()} placeholder="you@company.com" autoComplete="email" /></div>
              <div style={{ marginBottom:20 }}><label style={lbl}>Password</label><input style={inp} type="password" value={pw} onChange={e=>setPw(e.target.value)} onKeyDown={e=>e.key==="Enter"&&login()} placeholder="••••••••" autoComplete="current-password" /></div>
              <button onClick={login} disabled={busy} style={{ background:`linear-gradient(135deg,${MID},${SKY})`, color:"#fff", border:"none", padding:"13px 20px", borderRadius:8, fontWeight:700, fontSize:15, cursor:"pointer", fontFamily:"inherit", width:"100%", opacity:busy?.65:1 }}>
                {busy?"Signing in…":"Sign In to Portal"}
              </button>
              <div style={{ textAlign:"center", marginTop:14, fontSize:13, color:GRAY }}>No account? <span onClick={()=>setTab("req")} style={{ color:MID, fontWeight:600, cursor:"pointer" }}>Request access</span></div>
            </div>
          )}
          {tab==="req" && (rOk ? (
            <div style={{ background:"#ecfdf5", border:"1px solid #a7f3d0", borderRadius:10, padding:22, textAlign:"center" }}>
              <div style={{ fontSize:32, marginBottom:8 }}>✅</div>
              <div style={{ fontWeight:700, color:"#065f46", fontSize:15, marginBottom:6 }}>Request Submitted!</div>
              <div style={{ fontSize:13, color:"#047857", lineHeight:1.6 }}>Your request has been sent to the Tellemica team. We'll reach out within one business day to set up your account.</div>
              <div style={{ marginTop:14 }}><span onClick={()=>{setTab("in");setROk(false);}} style={{ color:MID, fontWeight:600, fontSize:13, cursor:"pointer" }}>← Back to Sign In</span></div>
            </div>
          ) : (
            <div>
              {rErr && <div style={{ background:"#fef2f2", border:"1px solid #fecaca", color:"#991b1b", borderRadius:7, padding:"9px 13px", fontSize:12, marginBottom:12 }}>{rErr}</div>}
              <div style={{ background:"#eff6ff", border:"1px solid #bfdbfe", color:"#1e40af", borderRadius:7, padding:"9px 13px", fontSize:12, marginBottom:14, lineHeight:1.5 }}>Access is managed by Tellemica. Fill this out and we'll set up your account within one business day.</div>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:12 }}>
                <div><label style={lbl}>First Name</label><input style={inp} value={r.first} onChange={e=>setR({...r,first:e.target.value})} placeholder="Jane" /></div>
                <div><label style={lbl}>Last Name</label><input style={inp} value={r.last} onChange={e=>setR({...r,last:e.target.value})} placeholder="Smith" /></div>
              </div>
              <div style={{ marginBottom:12 }}><label style={lbl}>Company</label><input style={inp} value={r.co} onChange={e=>setR({...r,co:e.target.value})} placeholder="Acme Corp" /></div>
              <div style={{ marginBottom:12 }}><label style={lbl}>Work Email</label><input style={inp} type="email" value={r.email} onChange={e=>setR({...r,email:e.target.value})} placeholder="jane@acme.com" /></div>
              <div style={{ marginBottom:12 }}><label style={lbl}>Phone</label><input style={inp} type="tel" value={r.phone} onChange={e=>setR({...r,phone:e.target.value})} placeholder="(610) 555-0000" /></div>
              <div style={{ marginBottom:20 }}>
                <label style={lbl}>I need access to</label>
                <select style={inp} value={r.reason} onChange={e=>setR({...r,reason:e.target.value})}>
                  <option value="">Select a reason…</option>
                  <option>Track open service orders</option>
                  <option>Manage account change requests</option>
                  <option>Monitor device deployments</option>
                  <option>Review billing &amp; plan changes</option>
                  <option>General account management</option>
                </select>
              </div>
              <button onClick={requestAccess} disabled={rBusy} style={{ background:`linear-gradient(135deg,${MID},${SKY})`, color:"#fff", border:"none", padding:"13px 20px", borderRadius:8, fontWeight:700, fontSize:15, cursor:"pointer", fontFamily:"inherit", width:"100%", opacity:rBusy?.65:1 }}>
                {rBusy?"Submitting…":"Submit Access Request"}
              </button>
              <div style={{ textAlign:"center", marginTop:12, fontSize:13, color:GRAY }}>Have an account? <span onClick={()=>setTab("in")} style={{ color:MID, fontWeight:600, cursor:"pointer" }}>Sign in</span></div>
            </div>
          ))}
        </div>
      </div>
      <ModeBadge />
      <InstallBanner />
    </div>
  );
}

// ─── DASHBOARD ────────────────────────────────────────────────────────────────
function Dashboard({ user, onLogout }) {
  const [tickets, setTickets]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [panel, setPanel]       = useState("all");
  const [visibleCount, setVC]   = useState(20);
  const [sortCol, setSortCol]   = useState("id");
  const [sortDir, setSortDir]   = useState("desc");

  // ── Read state is now stored as Zendesk tags (synced across all devices) ──
  // ── Read state: loaded from Supabase for cross-device sync ──────────────────
  // viewedTickets: { ticketId: ISO viewedAt string }
  const [viewedTickets, setViewedTickets] = useState(() => {
    // Seed from localStorage as instant fallback while Supabase loads
    try {
      const userId = String(user?.zendesk_id || user?.id || "");
      return JSON.parse(localStorage.getItem(`tlm_views_${userId}`) || "{}");
    } catch { return {}; }
  });

  const [detail, setDetail]     = useState(null);
  const [threadLoading, setTL]  = useState(false);
  const [modal, setModal]       = useState(false);
  const [mSub, setMSub]         = useState("");
  const [mCc, setMCc]           = useState([]);
  // Per-user CC email history for autocomplete/prefill. Local-only (localStorage),
  // keyed by user id, so one client's history is never visible to another.
  const [ccHistory, setCcHistory] = useState(() => {
    try {
      const uid = String(user?.zendesk_id || user?.id || "");
      return JSON.parse(localStorage.getItem(`tlm_cc_history_${uid}`) || "[]");
    } catch { return []; }
  });
  function rememberCcEmails(emails) {
    if (!emails || !emails.length) return;
    const uid = String(user.zendesk_id || user.id);
    setCcHistory(prev => {
      // Most-recent-first, deduplicated, keep all.
      const merged = [...new Set([...emails.map(e=>e.toLowerCase()), ...prev])];
      try { localStorage.setItem(`tlm_cc_history_${uid}`, JSON.stringify(merged)); } catch {}
      return merged;
    });
  }
  function forgetCcEmail(email) {
    const uid = String(user.zendesk_id || user.id);
    setCcHistory(prev => {
      const next = prev.filter(e => e !== email);
      try { localStorage.setItem(`tlm_cc_history_${uid}`, JSON.stringify(next)); } catch {}
      return next;
    });
  }
  const [mFile, setMFile]        = useState(null);
  const [mDesc, setMDesc]       = useState("");
  const [mPri, setMPri]         = useState("normal");
  const [mDone, setMDone]       = useState(false);
  const [mBusy, setMBusy]       = useState(false);
  const [reply, setReply]       = useState("");
  const [replying, setReplying] = useState(false);
  const [replyFile, setReplyFile] = useState(null);
  const [toast, setToast]       = useState("");
  const [search, setSearch]     = useState("");
  const [staffResults, setStaffResults] = useState(null); // null = not searching; array = results
  const [searching, setSearching] = useState(false);
  const isStaff = user.is_staff || user.role === "admin";
  const [err, setErr]           = useState("");
  const [showJumpBtn, setShowJumpBtn] = useState(false);
  const [showJumpTopBtn, setShowJumpTopBtn] = useState(false);
  const scrollRef = useRef(null);

  const showToast = msg => { setToast(msg); setTimeout(()=>setToast(""),3500); };
  const closeModal = () => { setModal(false); setMDone(false); setMSub(""); setMDesc(""); setMPri("normal"); setMCc([]); setMFile(null); };

  useEffect(()=>{ loadTickets(); },[]);

  // Staff search hits the backend (Zendesk Search API) so all tickets are
  // reachable. End users fall through to the instant local filter in getList().
  useEffect(() => {
    if (!isStaff) return;
    const q = search.trim();
    if (!q) { setStaffResults(null); setSearching(false); return; }
    setSearching(true);
    const handle = setTimeout(async () => {
      try {
        const results = await ZendeskAPI.searchTickets(user, q);
        setStaffResults(results);
      } catch { setStaffResults([]); }
      finally { setSearching(false); }
    }, 400);
    return () => clearTimeout(handle);
  }, [search, isStaff]);

  async function loadViewState() {
    try {
      const userId = String(user.zendesk_id || user.id);
      const res = await fetch(`${PROXY_URL}/read-state?user_id=${encodeURIComponent(userId)}`);
      if (!res.ok) return;
      const data = await res.json();
      if (data.views) {
        setViewedTickets(data.views);
        try { localStorage.setItem(`tlm_views_${userId}`, JSON.stringify(data.views)); } catch {}
      }
    } catch {}
  }

  async function markRead(ticketId, viewedAt) {
    try {
      const userId = String(user.zendesk_id || user.id);
      await fetch(`${PROXY_URL}/read-state`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId, ticket_id: String(ticketId), viewed_at: viewedAt })
      });
    } catch {}
  }

  // Load view state on mount
  useEffect(()=>{ loadViewState(); },[]);

  useEffect(()=>{
    const iv = setInterval(async()=>{
      loadTickets(true);
      loadViewState(); // sync read state across devices every 30s
      if (detail) {
        try {
          const result = await ZendeskAPI.getThread(detail.zendesk_id||detail.id);
          const thread = Array.isArray(result) ? result : result.comments;
          const collaborators = Array.isArray(result) ? [] : (result.collaborators || []);
          setDetail(p=>p?{...p,thread,collaborators}:p);
        } catch {}
      }
    },90000);
    return ()=>clearInterval(iv);
  },[detail]);

  async function loadTickets(preserveOptimistic=false) {
    setLoading(true); setErr("");
    try {
      const data = await ZendeskAPI.getTickets(user);
      if (preserveOptimistic) {
        // Keep any locally-added tickets not yet returned by Zendesk
        setTickets(prev => {
          const freshIds = new Set(data.map(t => String(t.id)));
          const optimistic = prev.filter(t => !freshIds.has(String(t.id)));
          return [...optimistic, ...data];
        });
      } else {
        setTickets(data);
      }
    }
    catch { setErr("Unable to load tickets. Please refresh or contact support."); }
    finally { setLoading(false); }
  }

  async function openTicket(t) {
    setDetail({...t,thread:[]}); setTL(true);

    const viewedAt = new Date().toISOString();
    const ticketId = String(t.id);
    const userId = String(user.zendesk_id || user.id);

    // Update local state instantly
    setViewedTickets(prev => {
      const updated = {...prev, [ticketId]: viewedAt};
      try { localStorage.setItem(`tlm_views_${userId}`, JSON.stringify(updated)); } catch {}
      return updated;
    });

    // Write to Supabase in background for cross-device sync
    markRead(ticketId, viewedAt);

    try {
      const result = await ZendeskAPI.getThread(t.zendesk_id||t.id);
      const thread = Array.isArray(result) ? result : result.comments;
      const collaborators = Array.isArray(result) ? [] : (result.collaborators || []);
      setDetail({...t, thread, collaborators});
    }
    catch { setDetail(t); }
    finally { setTL(false); }
  }

  const counts = {
    all:tickets.length, open:tickets.filter(t=>t.status==="open").length,
    pending:tickets.filter(t=>t.status==="pending").length, solved:tickets.filter(t=>t.status==="solved").length,
    new:tickets.filter(t=>t.status==="new").length, closed:tickets.filter(t=>t.status==="closed").length,
  };

  function getList() {
    // Staff + active search: use backend results (spans all tickets),
    // still filterable by the status panel and sortable.
    let r = (isStaff && staffResults !== null) ? [...staffResults] : [...tickets];
    if (panel==="open")         r=r.filter(t=>t.status==="open");
    else if (panel==="pending") r=r.filter(t=>t.status==="pending");
    else if (panel==="solved")  r=r.filter(t=>t.status==="solved");
    else if (panel==="new")     r=r.filter(t=>t.status==="new");
    else if (panel==="closed")  r=r.filter(t=>t.status==="closed");
    if (search.trim() && !(isStaff && staffResults !== null)) r=r.filter(t=>t.subject.toLowerCase().includes(search.toLowerCase())||t.id.toString().toLowerCase().includes(search.toLowerCase()));
    r.sort((a,b)=>{
      let va=a[sortCol]||"", vb=b[sortCol]||"";
      if (sortCol==="id"){va=parseInt(va);vb=parseInt(vb);}
      const cmp=va<vb?-1:va>vb?1:0;
      return sortDir==="asc"?cmp:-cmp;
    });
    return r;
  }

  async function submitTicket() {
    if (!mSub||!mDesc) { showToast("⚠️ Please fill in subject and description."); return; }
    setMBusy(true);
    try {
      const nt = await ZendeskAPI.createTicket(user,mSub,mDesc,"order",mPri,mCc.join(","),mFile);
      rememberCcEmails(mCc);
      setTickets(p=>[nt,...p]); setMDone(true);
      setTimeout(()=>{ closeModal(); showToast(`✅ Ticket ${nt.id} created — your Tellemica team has been notified.`); },1800);
    } catch { showToast("❌ Failed to submit. Please try again."); }
    finally { setMBusy(false); }
  }

  async function sendReply() {
    if (!reply.trim()||!detail) return;
    setReplying(true);
    try {
      await ZendeskAPI.addReply(detail.zendesk_id||detail.id,reply,user,replyFile);
      const now=new Date().toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"});
      const upd={...detail,updated:now,status:"open",thread:[...detail.thread,{from:user.name,role:"customer",time:now,body:reply}]};
      setTickets(p=>p.map(t=>t.id===upd.id?upd:t)); setDetail(upd); setReply(""); setReplyFile(null); showToast("✅ Reply sent!");
      setTimeout(()=>openTicket(upd),1500);
    } catch { showToast("❌ Failed to send reply. Please try again."); }
    finally { setReplying(false); }
  }

  const sideItems=[
    {key:"all",label:"All Tickets",count:counts.all},{key:"new",label:"New",count:counts.new},
    {key:"open",label:"Open",count:counts.open},{key:"pending",label:"Pending",count:counts.pending},
    {key:"solved",label:"Solved",count:null},{key:"closed",label:"Closed",count:null},
  ];
  const panelLabels={all:"All Tickets",new:"New",open:"Open",pending:"Pending",solved:"Solved",closed:"Closed"};
  const statItems=[["All",counts.all,"#0b1f3a","all"],["New",counts.new,"#a855f7","new"],["Open",counts.open,"#3b82f6","open"],["Pending",counts.pending,"#f59e0b","pending"],["Solved",counts.solved,"#10b981","solved"],["Closed",counts.closed,"#94a3b8","closed"]];

  // ── DETAIL VIEW ──
  const lastMsgRef = useRef(null);
  const firstMsgRef = useRef(null);

  function jumpToLatest() {
    if (lastMsgRef.current) lastMsgRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
    setShowJumpBtn(false);
  }

  function jumpToTop() {
    if (scrollRef.current) scrollRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    setShowJumpTopBtn(false);
  }

  const threadIsLong = (detail?.thread?.length || 0) >= 4;

  useEffect(() => {
    setShowJumpBtn(false);
    setShowJumpTopBtn(false);
    if (!detail || !threadIsLong) return;

    // Poll until the scroll element mounts (up to 1s)
    let attempts = 0;
    const interval = setInterval(() => {
      const scrollEl = scrollRef.current;
      attempts++;
      if (attempts > 10) { clearInterval(interval); return; }
      if (!scrollEl) return;
      clearInterval(interval);

      // Explicitly reset both buttons now that we have the element
      setShowJumpBtn(false);
      setShowJumpTopBtn(false);

      function onScroll() {
        const { scrollTop, scrollHeight, clientHeight } = scrollEl;
        const distFromBottom = scrollHeight - scrollTop - clientHeight;
        const BUFFER = 300;
        const atTop = scrollTop <= 0;
        const atBottom = distFromBottom <= 0;

        // Both hidden when at the very top or very bottom
        if (atTop || atBottom) {
          setShowJumpTopBtn(false);
          setShowJumpBtn(false);
          return;
        }

        setShowJumpTopBtn(scrollTop > BUFFER);
        setShowJumpBtn(distFromBottom > BUFFER);
      }

      scrollEl.addEventListener('scroll', onScroll, { passive: true });
      scrollEl._jumpCleanup = () => scrollEl.removeEventListener('scroll', onScroll);
    }, 100);

    return () => {
      clearInterval(interval);
      // Force hide both buttons on cleanup
      setShowJumpBtn(false);
      setShowJumpTopBtn(false);
      const scrollEl = scrollRef.current;
      if (scrollEl?._jumpCleanup) { scrollEl._jumpCleanup(); delete scrollEl._jumpCleanup; }
    };
  }, [detail?.id, detail?.thread?.length]);

  async function markAsUnread() {
    const userId = String(user.zendesk_id || user.id);
    const ticketId = String(detail.id);
    // Remove from local state immediately
    setViewedTickets(prev => {
      const updated = {...prev};
      delete updated[ticketId];
      try { localStorage.setItem(`tlm_views_${userId}`, JSON.stringify(updated)); } catch {}
      return updated;
    });
    // Delete from Supabase so all devices see it as unread
    if (LIVE_MODE) {
      fetch(`${PROXY_URL}/read-state`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: userId, ticket_id: ticketId })
      }).catch(() => {});
    }
    showToast("● Marked as unread");
    setDetail(null);
  }

  if (detail) return (
    <div className="tlm-shell">
      <GlobalStyles />
      <Header user={user} onLogout={onLogout} />
      <div className="tlm-header-spacer" />
      <div className="tlm-main-scroll" ref={scrollRef}>
        <div className="tlm-detail-wrap" style={{ maxWidth:820, margin:"0 auto", padding:"20px 16px", width:"100%" }}>
          <div style={{ display:"flex", justifyContent:"space-between", marginBottom:14, gap:8, flexWrap:"wrap" }}>
            <button onClick={()=>setDetail(null)} style={{ display:"inline-flex", alignItems:"center", gap:6, background:"#fff", border:`1.5px solid ${LGRAY}`, color:GRAY, padding:"9px 16px", borderRadius:8, fontSize:13, fontWeight:600, cursor:"pointer", fontFamily:"inherit" }}>← Back</button>
            <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
              <button onClick={markAsUnread} style={{ display:"inline-flex", alignItems:"center", gap:6, background:"#fff7ed", border:"1.5px solid #fed7aa", color:"#c2410c", padding:"9px 16px", borderRadius:8, fontSize:13, fontWeight:600, cursor:"pointer", fontFamily:"inherit" }}>● Mark as unread</button>
              <button onClick={()=>openTicket(detail)} style={{ display:"inline-flex", alignItems:"center", gap:6, background:"#fff", border:`1.5px solid ${LGRAY}`, color:GRAY, padding:"9px 16px", borderRadius:8, fontSize:13, fontWeight:600, cursor:"pointer", fontFamily:"inherit" }}>↻ Refresh</button>
            </div>
          </div>
          <div style={{ background:"#fff", borderRadius:12, padding:"18px 20px", boxShadow:"0 2px 10px rgba(0,0,0,.06)", marginBottom:14 }}>
            <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", gap:10, marginBottom:14 }}>
              <div style={{ fontWeight:700, fontSize:15, color:NAV, lineHeight:1.4, flex:1 }}>{detail.subject}</div>
              <StatusPill status={detail.status} />
            </div>
            <div style={{ display:"flex", flexWrap:"wrap", gap:18 }}>
              {[["Ticket ID",detail.id],["Opened",detail.opened],["Last Updated",detail.updated],["Priority",(PRIORITY[detail.priority]||PRIORITY.normal).label]].map(([k,v])=>(
                <div key={k}><div style={{ fontSize:9, fontWeight:700, letterSpacing:1, textTransform:"uppercase", color:GRAY, marginBottom:3 }}>{k}</div><div style={{ fontSize:12, fontWeight:600 }}>{v}</div></div>
              ))}
            </div>
            {detail.collaborators && detail.collaborators.length > 0 && (
              <div style={{ marginTop:12, paddingTop:12, borderTop:`1px solid ${LGRAY}` }}>
                <div style={{ fontSize:9, fontWeight:700, letterSpacing:1, textTransform:"uppercase", color:GRAY, marginBottom:6 }}>CC'd</div>
                <div style={{ display:"flex", flexWrap:"wrap", gap:5 }}>
                  {detail.collaborators.map((c,i) => (
                    <span key={i} style={{ display:"inline-flex", alignItems:"center", gap:4, background:"#eff6ff", color:MID, borderRadius:20, padding:"3px 10px", fontSize:12, fontWeight:600 }}>
                      ✉ {c.email||c.name||c}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
          {(detail.status==="solved"||detail.status==="closed") && (
            <div style={{ background:"#ecfdf5", border:"1px solid #a7f3d0", borderRadius:10, padding:"13px 16px", fontSize:13, color:"#065f46", display:"flex", alignItems:"center", gap:8, flexWrap:"wrap", marginBottom:14 }}>
              ✅ This ticket is {detail.status}.{" "}
              <span onClick={()=>{setMSub(`Follow-up on Ticket #${detail.id}`);setMDesc(`This is a follow-up request related to Ticket #${detail.id}.

`);setModal(true);}} style={{ color:MID, fontWeight:600, cursor:"pointer", textDecoration:"underline" }}>Need to follow up? Open a new request.</span>
            </div>
          )}
          <div style={{ background:"#fff", borderRadius:12, boxShadow:"0 2px 10px rgba(0,0,0,.06)", overflow:"hidden", marginBottom:14 }}>
            <div style={{ padding:"10px 16px", background:OFF, borderBottom:`1px solid ${LGRAY}`, fontSize:9, fontWeight:700, letterSpacing:2, textTransform:"uppercase", color:GRAY }}>Conversation Thread</div>
            {threadLoading ? <Spinner /> : detail.thread.length===0
              ? <div style={{ textAlign:"center", padding:28, color:GRAY, fontSize:13 }}>No messages yet.</div>
              : detail.thread.map((m,i)=>(
                <div key={i}
                  ref={i===0 ? firstMsgRef : i===detail.thread.length-1 ? lastMsgRef : null}
                  style={{ padding:"14px 18px", borderBottom:i<detail.thread.length-1?`1px solid ${OFF}`:"none", background:m.role==="agent"?"#f8faff":"#fff" }}>
                  <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:8, flexWrap:"wrap" }}>
                    <Av text={m.from.split(" ").map(w=>w[0]).join("").slice(0,2)} size={28} colors={m.role==="agent"?[NAV,MID]:["#64748b","#94a3b8"]} />
                    <div>
                      <span style={{ fontSize:13, fontWeight:700 }}>{m.from}</span>
                      {m.role==="agent"&&<span style={{ marginLeft:6, fontSize:10, background:"#eff6ff", color:MID, fontWeight:700, padding:"2px 6px", borderRadius:10, textTransform:"uppercase" }}>Tellemica</span>}
                    </div>
                    <span style={{ fontSize:11, color:GRAY, marginLeft:"auto" }}>{m.created_at ? formatLocalTime(m.created_at) : m.time}</span>
                  </div>
                  <div className="tlm-thread-body" style={{ fontSize:13, color:"#1e293b", lineHeight:1.7, paddingLeft:36, whiteSpace:"pre-wrap" }}>{m.body}</div>
                  {m.attachments && m.attachments.length > 0 && (
                    <div className="tlm-thread-body" style={{ paddingLeft:36, marginTop:8, display:"flex", flexWrap:"wrap", gap:8 }}>
                      {m.attachments.map((a,ai) => {
                        const proxyUrl = `/api/attachment?url=${encodeURIComponent(a.url)}`;
                        return (
                          <a key={ai} href={proxyUrl} download={a.filename} target="_blank" rel="noreferrer"
                            style={{ display:"inline-flex", alignItems:"center", gap:6, background:"#f1f5f9", border:"1px solid #e2e8f0", borderRadius:8, padding:"6px 12px", fontSize:12, color:"#1a56c4", fontWeight:600, textDecoration:"none" }}
                            onMouseEnter={e=>e.currentTarget.style.background="#e0e7ff"}
                            onMouseLeave={e=>e.currentTarget.style.background="#f1f5f9"}>
                            <span style={{ fontSize:16 }}>{a.isImage ? "🖼️" : a.contentType?.includes("pdf") ? "📄" : a.contentType?.includes("word") ? "📝" : a.contentType?.includes("excel") || a.contentType?.includes("sheet") ? "📊" : "📎"}</span>
                            <span>{a.filename}</span>
                            <span style={{ color:"#64748b", fontWeight:400 }}>({a.size ? (a.size/1024).toFixed(0)+"KB" : ""})</span>
                            <span style={{ fontSize:10, color:"#64748b" }}>↓</span>
                          </a>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))
            }
          </div>
          <div className="tlm-detail-bottom-pad">
            {detail.status!=="solved"&&detail.status!=="closed" ? (
              <div style={{ background:"#fff", borderRadius:12, padding:18, boxShadow:"0 2px 10px rgba(0,0,0,.06)" }}>
                <div style={{ fontSize:10, fontWeight:700, letterSpacing:1, textTransform:"uppercase", color:NAV, marginBottom:10 }}>Add a Reply</div>
                <textarea value={reply} onChange={e=>setReply(e.target.value)} placeholder="Type your reply here…" style={{ ...inp, height:85, resize:"vertical", display:"block" }} />
                <div style={{ marginTop:10 }}><FileAttachment file={replyFile} setFile={setReplyFile} /></div>
                <button className="tlm-reply-btn" onClick={sendReply} disabled={replying||!reply.trim()} style={{ marginTop:10, background:replying?GRAY:MID, color:"#fff", border:"none", padding:"11px 22px", borderRadius:8, fontWeight:700, fontSize:13, cursor:replying?"not-allowed":"pointer", fontFamily:"inherit", opacity:!reply.trim()?.5:1 }}>
                  {replying?"Sending…":"Send Reply →"}
                </button>
              </div>
            ) : (
              <div style={{ background:"#ecfdf5", border:"1px solid #a7f3d0", borderRadius:10, padding:"13px 16px", fontSize:13, color:"#065f46", display:"flex", alignItems:"center", gap:8, flexWrap:"wrap" }}>
                ✅ This ticket is {detail.status}.{" "}
                <span onClick={()=>{setMSub(`Follow-up on Ticket #${detail.id}`);setMDesc(`This is a follow-up request related to Ticket #${detail.id}.\n\n`);setModal(true);}} style={{ color:MID, fontWeight:600, cursor:"pointer", textDecoration:"underline" }}>Need to follow up? Open a new request.</span>
              </div>
            )}
          </div>
        </div>
      </div>
      {toast&&<div className="tlm-toast" style={{ position:"fixed", bottom:16, left:16, right:16, maxWidth:340, margin:"0 auto", background:NAV, color:"#fff", padding:"11px 15px", borderRadius:10, fontSize:13, boxShadow:"0 6px 20px rgba(0,0,0,.2)", zIndex:200 }}>{toast}</div>}
      {/* Jump to Top banner — appears when scrolled down past first message */}
      {showJumpTopBtn && (
        <div onClick={jumpToTop} style={{
          position:"fixed", top:72, left:"50%", transform:"translateX(-50%)",
          background:`linear-gradient(135deg, ${NAV}, #1a3d6b)`,
          color:"#fff", padding:"9px 20px", borderRadius:24,
          fontSize:13, fontWeight:700, whiteSpace:"nowrap",
          boxShadow:"0 4px 18px rgba(11,31,58,0.45)",
          display:"flex", alignItems:"center", gap:8,
          cursor:"pointer", zIndex:199,
          animation:"tlmSlideDown .3s ease-out",
          userSelect:"none",
        }}>
          ↑ Jump to top
        </div>
      )}
      {/* Jump to Latest banner — appears when scrolled up past last message */}
      {showJumpBtn && (
        <div onClick={jumpToLatest} style={{
          position:"fixed", bottom:24, left:"50%", transform:"translateX(-50%)",
          background:`linear-gradient(135deg, ${MID}, ${SKY})`,
          color:"#fff", padding:"9px 20px", borderRadius:24,
          fontSize:13, fontWeight:700, whiteSpace:"nowrap",
          boxShadow:"0 4px 18px rgba(26,86,196,0.45)",
          display:"flex", alignItems:"center", gap:8,
          cursor:"pointer", zIndex:199,
          animation:"tlmSlideUp .3s ease-out",
          userSelect:"none",
        }}>
          <span style={{ width:7, height:7, borderRadius:"50%", background:"rgba(255,255,255,0.7)", display:"inline-block", animation:"tlmPulse 1.5s infinite" }} />
          ↓ Jump to latest reply
        </div>
      )}
      <style>{`
        @keyframes tlmSlideUp   { from { transform: translateX(-50%) translateY(20px);  opacity:0; } to { transform: translateX(-50%) translateY(0); opacity:1; } }
        @keyframes tlmSlideDown { from { transform: translateX(-50%) translateY(-20px); opacity:0; } to { transform: translateX(-50%) translateY(0); opacity:1; } }
        @keyframes tlmPulse     { 0%,100% { opacity:1; } 50% { opacity:0.3; } }
      `}</style>
      <ModeBadge />
      {modal&&<Modal onClose={closeModal} mSub={mSub} setMSub={setMSub} mDesc={mDesc} setMDesc={setMDesc} mPri={mPri} setMPri={setMPri} mCc={mCc} setMCc={setMCc} mDone={mDone} mBusy={mBusy} onSubmit={submitTicket} mFile={mFile} setMFile={setMFile} ccHistory={ccHistory} onForgetCc={forgetCcEmail} />}
    </div>
  );

  const list = getList();

  // ── LIST VIEW ──
  return (
    <div className="tlm-shell">
      <GlobalStyles />
      <Header user={user} onLogout={onLogout} />
      <div className="tlm-header-spacer" />

      {/* Mobile sticky — fixed below header, outside scroll container */}
      <div className="tlm-mobile-sticky">
        <div className="tlm-stats-mobile">
          <div className="tlm-stats-mobile-inner">
            {statItems.map(([label,num,color,key])=>(
              <div key={label} onClick={()=>{setPanel(key);setSearch("");}} style={{ background:"#fff", borderRadius:10, padding:"10px 4px", boxShadow:"0 1px 6px rgba(0,0,0,.07)", borderTop:`3px solid ${color}`, cursor:"pointer", textAlign:"center", outline:panel===key?`2px solid ${color}`:"none" }}>
                <div style={{ fontWeight:800, fontSize:18, color:NAV, lineHeight:1 }}>{loading?"—":num}</div>
                <div style={{ fontSize:8, color:panel===key?color:GRAY, textTransform:"uppercase", letterSpacing:.5, marginTop:3, fontWeight:700, whiteSpace:"nowrap" }}>{label}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="tlm-mobile-search">
          <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="🔍  Search tickets…" style={{ ...inp, fontSize:14, padding:"10px 14px", flex:1 }} />
          <button onClick={loadTickets} style={{ flexShrink:0, background:"#fff", border:`1.5px solid ${LGRAY}`, color:GRAY, padding:"0 14px", borderRadius:8, fontSize:16, fontWeight:600, cursor:"pointer", fontFamily:"inherit" }}>↻</button>
        </div>
      </div>

      <div className="tlm-main-scroll" style={{ background:OFF }}>
        <div className="tlm-mobile-sticky-spacer" />
        <div className="tlm-dashboard">
          <aside className="tlm-sidebar">
            <div style={{ padding:12 }}>
              <button onClick={()=>setModal(true)} style={{ width:"100%", background:NAV, color:"#fff", border:"none", padding:"10px", borderRadius:7, fontWeight:700, fontSize:11, letterSpacing:1, textTransform:"uppercase", cursor:"pointer", fontFamily:"inherit" }}>+ New Request</button>
            </div>
            <div style={{ borderTop:`1px solid ${LGRAY}` }}>
              <div style={{ fontSize:9, fontWeight:700, letterSpacing:2, textTransform:"uppercase", color:GRAY, padding:"8px 16px 4px" }}>My Tickets</div>
              {sideItems.map(item=>(
                <div key={item.key} onClick={()=>{setPanel(item.key);setSearch("");setVC(20);}} style={{ display:"flex", alignItems:"center", gap:6, padding:"9px 16px", cursor:"pointer", fontSize:13, fontWeight:panel===item.key?700:400, color:panel===item.key?MID:GRAY, background:panel===item.key?OFF:"transparent", borderLeft:panel===item.key?`3px solid ${SKY}`:"3px solid transparent" }}>
                  {item.label}
                  {item.count!=null&&item.count>0&&<span style={{ marginLeft:"auto", background:item.key==="open"?MID:SKY, color:"#fff", fontSize:10, fontWeight:700, padding:"1px 6px", borderRadius:10 }}>{item.count}</span>}
                </div>
              ))}
            </div>
          </aside>

          <main style={{ minWidth:0, flex:1, minHeight:0 }}>
            <div className="tlm-desktop-sticky">
              <div className="tlm-stats-desktop">
                {statItems.map(([label,num,color,key])=>(
                  <div key={label} onClick={()=>{setPanel(key);setSearch("");setVC(20);}} style={{ background:"#fff", borderRadius:10, padding:"10px", boxShadow:"0 2px 8px rgba(0,0,0,.05)", borderLeft:`4px solid ${color}`, cursor:"pointer", outline:panel===key?`2px solid ${color}`:"none" }}>
                    <div style={{ fontWeight:800, fontSize:18, color:NAV, lineHeight:1 }}>{loading?"—":num}</div>
                    <div style={{ fontSize:9, color:panel===key?color:GRAY, textTransform:"uppercase", letterSpacing:1, marginTop:3, fontWeight:panel===key?700:400 }}>{label}</div>
                  </div>
                ))}
              </div>
              <div className="tlm-toolbar" style={{ alignItems:"center", justifyContent:"space-between", marginBottom:12, gap:8, flexWrap:"wrap" }}>
                <div style={{ fontWeight:800, fontSize:13, textTransform:"uppercase", letterSpacing:1, color:NAV }}>{panelLabels[panel]}</div>
                <div className="tlm-toolbar-right" style={{ display:"flex", gap:6, alignItems:"center" }}>
                  <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search tickets…" style={{ ...inp, width:180, padding:"7px 10px", fontSize:12 }} />
                  <button onClick={loadTickets} style={{ background:OFF, border:`1.5px solid ${LGRAY}`, color:GRAY, padding:"7px 12px", borderRadius:7, fontSize:11, fontWeight:600, cursor:"pointer", fontFamily:"inherit" }}>↻</button>
                  <button className="tlm-desktop-new-btn" onClick={()=>setModal(true)} style={{ background:MID, color:"#fff", border:"none", padding:"8px 14px", borderRadius:7, fontSize:11, fontWeight:700, letterSpacing:1, textTransform:"uppercase", cursor:"pointer", fontFamily:"inherit", whiteSpace:"nowrap" }}>+ New Request</button>
                </div>
              </div>
              <div className="tlm-table-header">
                {[["ID","id"],["Subject","subject"],["Priority","priority"],["Updated","updated"],["Status","status"]].map(([label,col])=>(
                  <div key={col} onClick={()=>{if(sortCol===col)setSortDir(d=>d==="asc"?"desc":"asc");else{setSortCol(col);setSortDir("asc");}}} style={{ fontSize:10, fontWeight:700, letterSpacing:1, textTransform:"uppercase", color:sortCol===col?MID:GRAY, cursor:"pointer", userSelect:"none" }}>
                    {label}<span style={{ opacity:.6 }}>{sortCol===col?(sortDir==="asc"?" ↑":" ↓"):" ↕"}</span>
                  </div>
                ))}
              </div>
            </div>

            {err&&<div style={{ background:"#fef2f2", border:"1px solid #fecaca", color:"#991b1b", borderRadius:8, padding:"10px 14px", marginBottom:14, fontSize:13 }}>{err} <span onClick={loadTickets} style={{ color:MID, cursor:"pointer", fontWeight:600 }}>Retry</span></div>}

            <div style={{ background:"#fff", borderRadius:12, boxShadow:"0 2px 10px rgba(0,0,0,.06)", overflow:"hidden" }}>
              {loading ? <Spinner /> : list.length===0
                ? <div style={{ textAlign:"center", padding:"36px 20px", color:GRAY, fontSize:13 }}>{search?`No results for "${search}"`:"No tickets in this view."}</div>
                : list.slice(0,visibleCount).map((t,i)=>{
                    const pri = PRIORITY[t.priority]||PRIORITY.normal;
                    const priLabel = t.priority==="urgent"?"🔴 Urgent":t.priority==="high"?"🟠 High":t.priority==="low"?"⚪ Low":"🔵 Normal";
                    const isUnread = (() => {
                      if (!t.updated_at) return false;
                      const viewedAt = viewedTickets[String(t.id)];
                      if (viewedAt) return t.updated_at > viewedAt;
                      // Never viewed — fall back to login time
                      const loginAt = user.loginAt || user.last_login;
                      if (loginAt) return t.updated_at > loginAt;
                      return false;
                    })();
                    return (
                      <div key={t.id}>
                        <div className="tlm-ticket-row" onClick={()=>openTicket(t)}
                          style={{ borderBottom:i<Math.min(visibleCount,list.length)-1?`1px solid ${OFF}`:"none", cursor:"pointer", background:isUnread?"#f0f7ff":"transparent" }}
                          onMouseEnter={e=>e.currentTarget.style.background="#f8faff"}
                          onMouseLeave={e=>e.currentTarget.style.background=isUnread?"#f0f7ff":"transparent"}>
                          <div style={{ fontSize:12, fontWeight:700, color:MID, display:"flex", alignItems:"center", gap:5 }}>
                            {isUnread && <span style={{ width:8, height:8, borderRadius:"50%", background:SKY, flexShrink:0, display:"inline-block" }} />}
                            {t.id}
                          </div>
                          <div style={{ minWidth:0 }}>
                            <div style={{ fontSize:13, fontWeight:isUnread?700:600, color:"#1e293b", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{t.subject}</div>
                            <div style={{ fontSize:11, color:GRAY, marginTop:2 }}>Updated {t.updated}</div>
                          </div>
                          <div style={{ fontSize:11, color:pri.color, fontWeight:600 }}>{priLabel}</div>
                          <div style={{ fontSize:11, color:GRAY }}>{t.updated}</div>
                          <div><StatusPill status={t.status} /></div>
                        </div>
                        <div className="tlm-ticket-card" onClick={()=>openTicket(t)}
                          style={{ borderLeft: isUnread ? `3px solid ${SKY}` : "3px solid transparent", background: isUnread ? "#f0f7ff" : "#fff" }}>
                          {/* Row 1: Ticket ID + unread dot (left) · Status pill (right) */}
                          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:5 }}>
                            <div style={{ display:"flex", alignItems:"center", gap:7 }}>
                              {isUnread && <span style={{ width:8, height:8, borderRadius:"50%", background:SKY, flexShrink:0, display:"inline-block" }} />}
                              <span style={{ fontSize:12, fontWeight:700, color:MID }}>{t.id}</span>
                            </div>
                            <StatusPill status={t.status} />
                          </div>
                          {/* Row 2: Subject */}
                          <div style={{ fontSize:14, fontWeight:isUnread?700:600, color:"#1e293b", lineHeight:1.4, marginBottom:5 }}>{t.subject}</div>
                          {/* Row 3: Updated date (left) · Priority small (right) */}
                          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                            <span style={{ fontSize:11, color:"#94a3b8" }}>Updated {t.updated}</span>
                            <span style={{ fontSize:11, fontWeight:600, color: t.priority==="urgent"?RED:t.priority==="high"?AMBER:"#94a3b8" }}>{priLabel}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })
              }
              {!loading&&list.length>visibleCount&&(
                <div style={{ textAlign:"center", padding:14, borderTop:`1px solid ${LGRAY}` }}>
                  <button onClick={()=>setVC(v=>v+10)} style={{ background:OFF, border:`1.5px solid ${LGRAY}`, color:NAV, padding:"9px 22px", borderRadius:7, fontSize:13, fontWeight:600, cursor:"pointer", fontFamily:"inherit" }}>Load More ({list.length-visibleCount} remaining)</button>
                </div>
              )}
            </div>
            {(user.is_staff || user.role==="admin")&&<div style={{ marginTop:10, background:"#fdf4ff", border:"1px solid #e9d5ff", borderRadius:8, padding:"7px 12px", fontSize:12, color:"#6b21a8" }}>👤 Staff view — showing all tickets. Search spans every ticket in the instance.</div>}
          </main>
        </div>
      </div>

      <button className="tlm-fab" onClick={()=>setModal(true)}>
        <span style={{ fontSize:20, lineHeight:1 }}>+</span> New Request
      </button>

      {modal&&<Modal onClose={closeModal} mSub={mSub} setMSub={setMSub} mDesc={mDesc} setMDesc={setMDesc} mPri={mPri} setMPri={setMPri} mCc={mCc} setMCc={setMCc} mDone={mDone} mBusy={mBusy} onSubmit={submitTicket} mFile={mFile} setMFile={setMFile} ccHistory={ccHistory} onForgetCc={forgetCcEmail} />}
      {toast&&<div className="tlm-toast" style={{ position:"fixed", bottom:16, left:16, right:16, maxWidth:340, margin:"0 auto", background:NAV, color:"#fff", padding:"11px 15px", borderRadius:10, fontSize:13, boxShadow:"0 6px 20px rgba(0,0,0,.2)", zIndex:99 }}>{toast}</div>}
      <ModeBadge />
    </div>
  );
}

// ─── ROOT ─────────────────────────────────────────────────────────────────────
export default function App() {
  // ── FIX 2: persist session to localStorage so back button doesn't log out ──
  const [user, setUser] = useState(() => {
    try {
      const saved = localStorage.getItem("tlm_user");
      if (!saved) return null;
      const u = JSON.parse(saved);
      // Keep existing lastActiveAt — don't reset it on page load.
      // This ensures tickets updated since the last session show as unread.
      return u;
    } catch { return null; }
  });

  function handleLogin(u) {
    // Preserve lastActiveAt from any previous session so we know what was
    // already seen. Only set it fresh if this is a brand new user (no prior session).
    const existingSession = (() => {
      try { return JSON.parse(localStorage.getItem("tlm_user") || "null"); } catch { return null; }
    })();
    const normalized = {
      ...u,
      org_id: u.org_id || u.organization_id,
      zendesk_id: u.zendesk_id || u.id,
      initials: u.initials || (u.name ? u.name.split(' ').map(w=>w[0]).join('').slice(0,2).toUpperCase() : '?'),
      org: u.org || u.organization || 'Tellemica Client',
      // Use prior session's lastActiveAt so updates since last visit show as unread.
      // Also check tlm_lastActive for users who explicitly logged out.
      // If no prior session at all, use now (first ever login).
      loginAt: existingSession?.lastActiveAt || existingSession?.loginAt || localStorage.getItem("tlm_lastActive") || new Date().toISOString(),
      lastActiveAt: new Date().toISOString(),
    };
    localStorage.setItem("tlm_user", JSON.stringify(normalized));
    setUser(normalized);
  }

  function handleLogout() {
    // Save lastActiveAt before wiping session so next login
    // can use it to determine what's unread since last visit.
    try {
      const u = JSON.parse(localStorage.getItem("tlm_user") || "null");
      if (u) {
        localStorage.setItem("tlm_lastActive", new Date().toISOString());
      }
    } catch {}
    localStorage.removeItem("tlm_user");
    setUser(null);
  }

  return (
    <div className="tlm-shell">
      <GlobalStyles />
      {user ? <Dashboard user={user} onLogout={handleLogout} /> : <Login onLogin={handleLogin} />}
    </div>
  );
}
